/**
 * Aritmética de venda, custo e lucro a partir do snapshot da cobrança.
 *
 * Espelha `apps/backend/src/modules/core/reports/reports-financials.ts`. Regra central:
 * **nada aqui pode ler o catálogo** — preço de catálogo é editável, e usá-lo faria uma
 * venda antiga aparecer com o preço de hoje. Só snapshot gravado no momento da venda.
 *
 * ⚠️ `Billing.amount` **já inclui** `feeAmount` (a taxa do gateway). A receita líquida
 * da empresa é `amount - feeAmount`.
 */

/** Campos do `Billing` necessários para apurar custo e receita líquida. */
export interface BillingFinancialSnapshot {
    amount: number
    /**
     * Valor faturado ao convênio. Presente só quando `payerType = HEALTH_PLAN`, e
     * nesse caso **é ele a receita** — o `amount` vale zero de propósito (quem cobra
     * o convênio é a fatura do período). Ver billing-system.md §1.1.
     */
    invoicedAmount?: number | null
    payerType?: string | null
    feeAmount?: number | null
    type?: string | null
    partnerId?: number | null
    professionalId?: number | null
    hospitalId?: number | null
    /**
     * Rateio por atendimento. Existe quando a cobrança cobre mais de um: aí os
     * campos escalares acima ficam NULOS de propósito (não existe "o parceiro" de
     * uma cobrança com três) e a verdade mora aqui.
     * Ver docs/billing-payout-share.md.
     */
    /**
     * Cobranças penduradas nesta (forma de pagamento dividida, complemento). Numa
     * cobrança que é só de UM atendimento, elas são parte da mesma venda — ver
     * `netRevenue`. Ausente quando quem carregou não as pediu.
     */
    adjustments?: Array<{
        amount: number
        feeAmount?: number | null
        status?: string | null
        refundedAmount?: number | null
    }> | null
    /** Quanto já foi devolvido ao cliente, ACUMULADO (ver `keptRatioOf`). */
    refundedAmount?: number | null
    payoutShares?: PayoutShare[] | null
}

/** Fatia de UM atendimento dentro da cobrança. */
export interface PayoutShare {
    sourceType: string
    sourceId: number
    /** Quanto deste atendimento entra no `amount` da cobrança. */
    amount: number
    partnerId?: number | null
    professionalId?: number | null
    hospitalId?: number | null
    partnerAmount?: number | null
    professionalAmount?: number | null
    hospitalAmount?: number | null
    suppliesAmount?: number | null
}

/**
 * Receita e custo do PROCEDIMENTO inteiro, apurados pelo relatório do backend
 * (`computeProcedureFinancials`): anestesista, OPME e produto de pacote ficam fora da
 * fatia, o procedimento pode estar pago em várias cobranças ou não ter nenhuma.
 */
export interface ProcedureTotals {
    netRevenue?: number
    grossRevenue?: number
    cost: number | null
}

/** Qual atendimento originou a linha. */
export interface AppointmentSource {
    sourceType: string
    sourceId: number
    /** Totais do procedimento, quando a linha é um. Espelha o backend. */
    procedureTotals?: ProcedureTotals | null
}

/** A cobrança cobre mais de um atendimento? */
export function isSharedBilling(
    billing?: BillingFinancialSnapshot | null,
): boolean {
    return (billing?.payoutShares?.length ?? 0) > 1
}

/**
 * A fatia DESTE atendimento, quando a cobrança cobre mais de um.
 *
 * ⚠️ É o que impede os valores de multiplicarem. Uma cobrança de R$ 900 que paga
 * três atendimentos fica pendurada nos TRÊS: sem separar a fatia, cada tela mostra
 * R$ 900 como se fosse daquele atendimento.
 *
 * `undefined` = usar a cobrança inteira (um atendimento só, ou a tela da própria
 * cobrança). `null` = é de um atendimento, mas a fatia dele não foi encontrada:
 * melhor "não apurado" do que atribuir a ele o valor dos outros.
 */
function shareFor(
    billing: BillingFinancialSnapshot,
    source?: AppointmentSource | null,
): PayoutShare | null | undefined {
    const shares = billing.payoutShares
    if (!shares || shares.length <= 1) return undefined
    if (!source) return undefined
    return (
        shares.find(
            s =>
                s.sourceType === source.sourceType &&
                s.sourceId === source.sourceId,
        ) ?? null
    )
}

/** Formata centavos. Renderiza zero como R$ 0,00 (e não como "-"). */
export const formatMoney = (centavos: number) =>
    new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    }).format(centavos / 100)

/** "—" quando o valor não é apurável; nunca "R$ 0,00" para desconhecido. */
export const moneyOrDash = (value: number | null | undefined) =>
    value == null ? '—' : formatMoney(value)

/** Receita líquida: o que foi cobrado menos a taxa do gateway. */
/** Os totais do procedimento, quando a linha é um. Espelha o backend. */
function procedureTotalsFor(
    source?: AppointmentSource | null,
): ProcedureTotals | undefined {
    if (source?.sourceType !== 'PROCEDURE') return undefined
    return source.procedureTotals ?? undefined
}

/**
 * Quanto da cobrança FICOU depois do estorno: 1 sem estorno, 0 estornada por inteiro.
 * Estorno é dinheiro devolvido e desconta a venda do atendimento. Espelha o backend
 * (`appointment-financials.ts`), onde está o porquê.
 */
function keptRatioOf(charge: { amount: number; refundedAmount?: number | null }): number {
    const refunded = charge.refundedAmount ?? 0
    if (refunded <= 0 || charge.amount <= 0) return 1
    return Math.max(0, (charge.amount - refunded) / charge.amount)
}

export function netRevenue(
    billing?: BillingFinancialSnapshot | null,
    source?: AppointmentSource | null,
): number | null {
    const procedure = procedureTotalsFor(source)
    if (procedure?.netRevenue !== undefined) return procedure.netRevenue

    if (!billing) return null

    // COBERTO POR CONVÊNIO: a receita é o valor faturado ao convênio, não o `amount`
    // — que é zero de propósito. Sem isto a tela mostrava "Venda (líquida) R$ 0,00"
    // contra o custo real do repasse, e o Lucro saía NEGATIVO no valor inteiro do
    // repasse (exame #71685: custo R$ 1.100, venda R$ 0,00, lucro −R$ 1.100).
    // Convênio não tem taxa de gateway, então não há o que descontar.
    if (billing.invoicedAmount != null) {
        return billing.invoicedAmount
    }

    const share = shareFor(billing, source)
    if (share === null) return null
    if (share) {
        // A taxa é da cobrança inteira; cada atendimento carrega a parte dele, na
        // proporção do que representa no total.
        const bruto = share.amount ?? 0
        const fee = billing.feeAmount ?? 0
        const proporcional =
            fee > 0 && billing.amount > 0
                ? Math.round((fee * bruto) / billing.amount)
                : 0
        // O estorno da cobrança compartilhada entra na fatia, na mesma proporção.
        return Math.round((bruto - proporcional) * keptRatioOf(billing))
    }

    // Cobrança só DESTE atendimento: as penduradas nela (forma de pagamento dividida,
    // complemento) são parte da mesma venda. Ler só a principal contava R$ 80 de um
    // exame de R$ 580 pago em cartão + PIX (exame 72183) — agosto/2026 saiu R$ 2,6
    // mil a menos em exames e consultas. Só para a linha de ATENDIMENTO (`source`): no
    // relatório de lançamentos a linha É a cobrança, e a pendurada é outra linha.
    const ownGross = billing.amount - (billing.feeAmount ?? 0)
    if (!source) return ownGross
    const own = Math.round(ownGross * keptRatioOf(billing))
    if (!billing.adjustments?.length) return own
    return billing.adjustments
        .filter(a => a.status !== 'CANCELLED')
        .reduce((sum, a) => sum + Math.round((a.amount - (a.feeAmount ?? 0)) * keptRatioOf(a)), own)
}

/**
 * A fatia tem custo NÃO apurado? Só quando há parceiro sem valor.
 *
 * Profissional e hospital sem valor são repasse zero: o profissional pode não ter
 * preço (procedimento, profissional da casa) ou ter o repasse suprimido na
 * intermediação, e o hospital da consulta é só o local. Espelha
 * `isShareCostUnknown` do backend — as duas contas não podem divergir.
 */
export function isShareCostUnknown(share: {
    partnerId?: number | null
    partnerAmount?: number | null
}): boolean {
    return share.partnerId != null && share.partnerAmount == null
}

/**
 * Custo apurado (soma dos repasses congelados), ou `null` quando **desconhecido**.
 *
 * Só o PARCEIRO sem valor torna o custo desconhecido (`isShareCostUnknown`).
 */
export function resolveCost(
    billing?: BillingFinancialSnapshot | null,
    source?: AppointmentSource | null,
): number | null {
    if (billing?.type === 'PAYABLE') return null

    const procedure = procedureTotalsFor(source)
    if (procedure) return procedure.cost

    if (!billing) return null

    // Cobrança compartilhada: o custo desta tela é o da FATIA. Somar todas daria o
    // custo dos três atendimentos na página de um só.
    const share = shareFor(billing, source)
    if (share === null) return null
    if (share) {
        if (isShareCostUnknown(share)) return null
        return (
            (share.partnerAmount ?? 0) +
            (share.professionalAmount ?? 0) +
            (share.hospitalAmount ?? 0) +
            (share.suppliesAmount ?? 0)
        )
    }

    // Sem rateio: a cobrança inteira. Vale também para a tela da própria cobrança,
    // onde a linha É a cobrança e o total é o número certo.
    if (billing.payoutShares?.length) {
        if (billing.payoutShares.some(isShareCostUnknown)) return null
        return billing.payoutShares.reduce(
            (total, sh) =>
                total +
                (sh.partnerAmount ?? 0) +
                (sh.professionalAmount ?? 0) +
                (sh.hospitalAmount ?? 0) +
                (sh.suppliesAmount ?? 0),
            0,
        )
    }

    // Sem fatia nenhuma: não há repasse a fazer, custo zero. Espelha
    // `reports-financials.ts` do backend — as duas contas não podem divergir.
    return 0
}

/** Lucro = receita líquida − custo apurado. `null` se qualquer um for desconhecido. */
export function resolveProfit(
    billing?: BillingFinancialSnapshot | null,
    source?: AppointmentSource | null,
): number | null {
    const revenue = netRevenue(billing, source)
    const cost = resolveCost(billing, source)
    if (revenue == null || cost == null) return null
    return revenue - cost
}

/** Margem de lucro em % (1 casa decimal). `null` se não apurável. */
export function profitMargin(revenue: number, profit: number): number | null {
    if (revenue <= 0) return null
    return Math.round((profit / revenue) * 1000) / 10
}
